import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import GUI from 'lil-gui';
import Stats from 'stats.js';
import CountdownTimer from './timer.js';
import Fireworks from './world/fireworks.js';
import { Ball } from './world/ball.js';
import Ground from './world/ground.js';
import YearSign from './world/yearsign.js';
import { POLE_HEIGHT, PLATFORM_HEIGHT, PolePlatform } from './world/pole-platform.js';

let instance = null

export default class Experience {
    constructor(_canvas) {
        // Singleton
        if (instance) {
            return instance;
        }
        instance = this;
        window.experience = this; // Global access

        // Options
        this.canvas = _canvas;

        this.settings = {
            fpsCounter: false,
            sound: true,
        }
        
        const debugActions = {
            timer1Min: () => {
                this.countdownTimer.jumpTimerSecLeft(62);
            },
            timer10Sec: () => {
                this.countdownTimer.jumpTimerSecLeft(11);
            }
        }
        
        /**
         * Base
         */
        this.stats = new Stats();
        this.stats.showPanel(0); // 0: fps, 1: ms, 2: mb, 3+: custom
        
        // Debug
        this.gui = new GUI({ width: 340 });
        this.gui.add(this.settings, 'fpsCounter')
            .name('FPS Counter')
            .onFinishChange((value) => {
                if (value) {
                    document.body.appendChild(this.stats.dom);
                }
                else {
                    document.body.removeChild(this.stats.dom);
                }
            });
        this.gui.add(this.settings, 'sound').name('Sound')
            .onFinishChange((value) => {
                if (!value && this.music) {
                    this.music.pause();
                }
            });
        this.gui.add(debugActions, 'timer1Min').name('1 Min Timer');
        this.gui.add(debugActions, 'timer10Sec').name('10 Sec Timer');
        this.gui.hide();
        
        // Canvas
        const canvas = document.querySelector('canvas.webgl');
        
        // Scene
        this.scene = new THREE.Scene();
        
        // Loaders
        this.loadingManager = new THREE.LoadingManager();
        const statusDiv = document.getElementById('loadingstatus');
        const loadingMessage = (msg) => {
            while (statusDiv.hasChildNodes()) statusDiv.removeChild(statusDiv.firstChild);
            let text = document.createTextNode(msg);
            statusDiv.appendChild(text);
        }
        this.loadingManager.onStart = () => {
            loadingMessage('Loading...');
        }
        this.loadingManager.onProgress = (url, itemsLoaded, itemsTotal) => {
            const progress =  new Number(itemsLoaded / itemsTotal * 100.0).toFixed(0);
            loadingMessage('Loading ' + progress + '%');
        }
        this.loadingManager.onLoad = () => {
            statusDiv.parentNode.removeChild(statusDiv);
        }
        this.textureLoader = new THREE.TextureLoader(this.loadingManager);
        this.audioLoader = new THREE.AudioLoader(this.loadingManager);
        this.gltfLoader = new GLTFLoader(this.loadingManager);
        
        /**
         * Sizes
         */
        this.sizes = {
            width: window.innerWidth,
            height: window.innerHeight,
            pixelRatio: Math.min(window.devicePixelRatio, 2)
        };
        this.sizes.resolution = new THREE.Vector2(
            this.sizes.width * this.sizes.pixelRatio, 
            this.sizes.height * this.sizes.pixelRatio
        );
        
        window.addEventListener('resize', () => {
            // Update sizes
            this.sizes.width = window.innerWidth;
            this.sizes.height = window.innerHeight;
            this.sizes.pixelRatio = Math.min(window.devicePixelRatio, 2);
            this.sizes.resolution.set(
                this.sizes.width * this.sizes.pixelRatio,
                this.sizes.height * this.sizes.pixelRatio
            );
        
            // Update camera
            this.camera.aspect = this.sizes.width / this.sizes.height
            this.camera.updateProjectionMatrix()
        
            // Update renderer
            this.renderer.setSize(this.sizes.width, this.sizes.height);
            this.renderer.setPixelRatio(this.sizes.pixelRatio);
            this.effectComposer.setSize(this.sizes.width, this.sizes.height)
            this.effectComposer.setPixelRatio(this.sizes.pixelRatio)
        });
        const fullscreenToggle = () => {
            if (!document.fullscreenElement) {
                document.body.requestFullscreen();
            }
            else {
                document.exitFullscreen();
            }
        };
        canvas.addEventListener('dblclick', (e) => {
            fullscreenToggle()
        });
        window.addEventListener('keydown', (e) => {
            switch (e.key) {
            case 'f':
                fullscreenToggle();
                break;
            case 'h':
                this.gui.show(this.gui._hidden);
                break;
            case '3':
                this.countdownTimer.jumpTimerSecLeft(3);
                break;
            case '1':
                this.countdownTimer.jumpTimerSecLeft(62);
                break;
            case '0':
                this.countdownTimer.jumpTimerSecLeft(12);
                break;
            case '-':
                this.countdownTimer.adjustTimeToServer();
                break;
            case ' ':
                this.fireworks.createRandomFirework();
                break;
            }
        });``

        const ambientLight = new THREE.AmbientLight(0xb9d5ff, 0.1);
        this.scene.add(ambientLight);
        const lightingGui = this.gui.addFolder('Lighting')
        lightingGui.close();
        lightingGui.add(ambientLight, 'intensity').min(0).max(20).step(0.001).name('Ambient Light')

        const directionalLight = new THREE.DirectionalLight(0xb9d5ff, 0.25);
        directionalLight.position.set(-150, 90, -134.8)
        directionalLight.castShadow = true
        directionalLight.shadow.mapSize.width = 1024
        directionalLight.shadow.mapSize.height = 1024
        directionalLight.shadow.camera.near = 7
        directionalLight.shadow.camera.far = 21
        directionalLight.shadow.camera.top = 5
        directionalLight.shadow.camera.right = 5
        directionalLight.shadow.camera.bottom = -5
        directionalLight.shadow.camera.left = -5
        this.scene.add(directionalLight)

        lightingGui.add(directionalLight, 'intensity').min(0).max(20).step(0.001).name('Directional Light')
        lightingGui.add(directionalLight.position, 'x').min(- 30).max(30).step(0.01).name('lightX')
        lightingGui.add(directionalLight.position, 'y').min(- 30).max(30).step(0.01).name('lightY')
        lightingGui.add(directionalLight.position, 'z').min(- 30).max(30).step(0.01).name('lightZ')
        // lightingGui.add(directionalLight.shadow, 'normalBias').min(- 0.05).max(0.05).step(0.001)
        // lightingGui.add(directionalLight.shadow, 'bias').min(- 0.05).max(0.05).step(0.001)

        // this.exrLoader.load('./envmap/NightSkyHDRI012_2K-HDR.exr', (environmentMap) => {
        //     environmentMap.mapping = THREE.EquirectangularReflectionMapping
        //     this.scene.background = environmentMap
        //     this.scene.environment = environmentMap
        // })
        
        //const environmentMap = this.textureLoader.load('envmap/NightSkyHDRI012_4K-TONEMAPPED2_recomp.jpg');
        // const environmentMap = this.textureLoader.load('envmap/NightSkyHDRI012_8K-TONEMAPPED.jpg');
        // environmentMap.mapping = THREE.EquirectangularReflectionMapping
        // environmentMap.colorSpace = THREE.SRGBColorSpace
        // this.scene.background = environmentMap
        

        const loader = new THREE.CubeTextureLoader();
        loader.setPath( 'envmap/' );
        const textureCube = loader.load( [
            'tcube.jpg', 'tcube.jpg',
            'tcube.jpg', 'tcube.jpg',
            'tcube.jpg', 'tcube.jpg',
        ] );
        this.scene.background = textureCube;

        //this.scene.environment = environmentMap
        //this.scene.environmentIntensity = 0.1;
        //lightingGui.add(this.scene, 'environmentIntensity').min(0).max(1).step(0.001).name('Env Map Intensity')
        this.scene.fog = new THREE.Fog('#040404', 100, 450)
        
        
        /**
         * Camera
         */
        // Base camera
        this.camera = new THREE.PerspectiveCamera(25, this.sizes.width / this.sizes.height, 0.1, 1000)
        this.camera.position.set(0, 10 , 110)
        this.scene.add(this.camera)
        
        // Controls
        this.controls = new OrbitControls(this.camera, canvas)
        this.controls.enableDamping = true
        this.controls.maxPolarAngle = Math.PI * 0.53;
        this.controls.minDistance = 32;
        this.controls.maxDistance = 150;

        // Audio
        this.listener = new THREE.AudioListener();
        this.camera.add(this.listener);
        
        /**
         * Renderer
         */
        this.renderer = new THREE.WebGLRenderer({
            canvas: canvas,
            antialias: true
        })
        this.renderer.setSize(this.sizes.width, this.sizes.height)
        this.renderer.setPixelRatio(this.sizes.pixelRatio)

        /**
         * Post processing
         */
        const renderTarget = new THREE.WebGLRenderTarget(
            800,
            600,
            {
                samples: (2 - this.sizes.pixelRatio) * 2 // pxRatio of 2 -> 0, 1 -> 2
            }
        )
        this.effectComposer = new EffectComposer(this.renderer, renderTarget);
        this.effectComposer.setPixelRatio(this.sizes.pixelRatio);
        this.effectComposer.setSize(this.sizes.width, this.sizes.height);
        this.renderPass = new RenderPass(this.scene, this.camera);
        this.effectComposer.addPass(this.renderPass);

        
        const unrealBloomPass = new UnrealBloomPass();
        unrealBloomPass.enabled = true;
        unrealBloomPass.strength = 0.5;
        unrealBloomPass.radius = 0.1;
        unrealBloomPass.threshold = 0.225;

        const postGui = this.gui.addFolder('Bloom Effect')
        postGui.close();
        postGui.add(unrealBloomPass, 'enabled');
        postGui.add(unrealBloomPass, 'strength').min(0).max(10).step(0.001);
        postGui.add(unrealBloomPass, 'radius').min(0).max(2).step(0.001);
        postGui.add(unrealBloomPass, 'threshold').min(0).max(1).step(0.001);
        this.effectComposer.addPass(unrealBloomPass);

        this.clock = new THREE.Clock()
        this.countdownTimer = new CountdownTimer({
            displayDivId: 'countdown-display',
            onTimerEnd: () => {
                this.yearSign.lightOn();
                this.ball.lightOff();
                window.setTimeout(() => {
                    // Ball turns back on (slowly) 4 minutes after midnight
                    this.ball.lightOn(10);
                }, 4*60*1000);
                
                // Start shooting fireworks
                window.setTimeout(() => {
                    // start the fireworks strong
                    for (let i = 0; i < 4; i++) {
                        this.fireworks.createRandomFirework();
                    }
                    this.fireworks.start(); // continual fireworks
                }, 500);

                // Music starts 4 seconds later
                this.playMusic(4000);
            }
        });
        this.ball = new Ball();
        this.polePlatform = new PolePlatform();
        this.ground = new Ground();
        this.yearSign = new YearSign(this.countdownTimer.getTimerYear());
        this.fireworks = new Fireworks();

        // Is it already the new year on page load?
        if (this.countdownTimer.getTimeLeft() < 0) {
            // There is a small chance these methods could be called twice so they must be idempotent
            this.yearSign.lightOn(0);
            this.fireworks.start();
        }

        this.controls.target = new THREE.Vector3( 0, POLE_HEIGHT / 2.0 + PLATFORM_HEIGHT, 0 );
        this.update();
    }

    resize() {
        this.camera.resize()
        this.renderer.resize()
    }

    update() {
        this.stats.begin();
        const elapsedTime = this.clock.getElapsedTime()
        
        const timeLeft = this.countdownTimer.update();
        this.ball.update(elapsedTime, timeLeft);

        // Update controls
        this.controls.update();
        
        // Render
        this.effectComposer.render(this.scene, this.camera);
    
        this.stats.end();
    
        // Call tick again on the next frame
        window.requestAnimationFrame(() => {
            this.update();
        });
    }

    playMusic(playAfterDelay) {
        const audioTest = document.createElement('audio');
        if (!this.settings.sound
            || !audioTest.canPlayType
            || '' == audioTest.canPlayType('audio/ogg; codecs=\"opus\"')) {
            return;
        }
        const start = Date.now() ;
        const This = this;
        if (!this.music) {
            this.music = new Audio('./auld_lang_syne48.opus');
            this.music.addEventListener('canplaythrough', function() { 
                const delayTimeLeft = Math.max(0, playAfterDelay - (Date.now() - start));
                window.setTimeout(() => {
                    // Setting currentTime = 0 here causes infinite loop!
                    This.music.play();
                }, delayTimeLeft);
             }, false);
        }
        else {
            // Song already loaded
            window.setTimeout(() => {
                This.music.currentTime = 0;
                This.music.play();
            }, playAfterDelay);
        }
    }
}